import React, { useRef, useEffect } from 'react';

import { getSlideDate, handleSlideAnimationEnd, animateContent } from '../shared/sliderHelpers';
import {
  deepCloneObject,
  isSameDay,
  createUniqueRange,
  getValueType,
} from '../shared/generalUtils';
import { TYPE_SINGLE_DATE, TYPE_RANGE, TYPE_MUTLI_DATE } from '../shared/constants';
import handleKeyboardNavigation from '../shared/keyboardNavigation';
import { useLocaleUtils, useLocaleLanguage } from '../shared/hooks';

const DaysList = ({
  activeDate,
  value,
  monthChangeDirection,
  onSlideChange,
  disabledDays = [],
  onDisabledDayError = () => {},
  minimumDate,
  maximumDate,
  onChange = () => {},
  locale,
  calendarTodayClassName = '',
  calendarSelectedDayClassName = '',
  calendarRangeStartClassName = '',
  calendarRangeEndClassName = '',
  calendarRangeBetweenClassName = '',
  shouldHighlightWeekends = false,
  isQuickSelectorOpen,
  customDaysClassName,
}) => {
  const calendarSectionWrapper = useRef(null);
  const { isRtl, weekDays: weekDaysList } = useLocaleLanguage(locale);
  const {
    getToday,
    isBeforeDate,
    checkDayInDayRange,
    getMonthFirstWeekday,
    getMonthLength,
    getLanguageDigits,
    getMonthName,
  } = useLocaleUtils(locale);
  const today = getToday();

  useEffect(() => {
    if (!monthChangeDirection) return;
    animateContent({
      direction: monthChangeDirection,
      parent: calendarSectionWrapper.current,
    });
  }, [monthChangeDirection]);

  const getDayRangeValue = day => {
    const clonedDayRange = deepCloneObject(value);
    const dayRangeValue =
      clonedDayRange.from && clonedDayRange.to ? { from: null, to: null } : clonedDayRange;
    const dayRangeProp = !dayRangeValue.from ? 'from' : 'to';
    dayRangeValue[dayRangeProp] = day;
    const { from, to } = dayRangeValue;

    if (isBeforeDate(to, from)) {
      dayRangeValue.from = to;
      dayRangeValue.to = from;
    }

    const includingDisabledDay = disabledDays.find(disabledDay =>
      checkDayInDayRange({
        day: disabledDay,
        from: dayRangeValue.from,
        to: dayRangeValue.to,
      }),
    );
    if (includingDisabledDay) {
      onDisabledDayError(includingDisabledDay);
      return value;
    }

    return dayRangeValue;
  };

  const getMultiDateValue = day => {
    const isAlreadyExisting = value.some(valueDay => isSameDay(valueDay, day));
    return isAlreadyExisting
      ? value.filter(valueDay => !isSameDay(valueDay, day))
      : [...value, day];
  };

  const handleDayClick = day => {
    const valueType = getValueType(value);
    const newValue =
      valueType === TYPE_SINGLE_DATE
        ? day
        : valueType === TYPE_RANGE
        ? getDayRangeValue(day)
        : getMultiDateValue(day);
    onChange(newValue);
  };

  const isSingleDateSelected = day => {
    const valueType = getValueType(value);
    if (valueType === TYPE_SINGLE_DATE) return isSameDay(day, value);
    if (valueType === TYPE_MUTLI_DATE) return value.some(valueDay => isSameDay(valueDay, day));
  };

  const getDayStatus = dayItem => {
    const isToday = isSameDay(dayItem, today);
    const isSelected = isSingleDateSelected(dayItem);
    const { from, to } = value || {};
    const isStartingDayRange = isSameDay(dayItem, from);
    const isEndingDayRange = isSameDay(dayItem, to);
    const isWithinRange = checkDayInDayRange({ day: dayItem, from, to });
    return { isToday, isSelected, isStartingDayRange, isEndingDayRange, isWithinRange };
  };

  const getDayClassNames = dayItem => {
    const {
      isToday,
      isSelected,
      isStartingDayRange,
      isEndingDayRange,
      isWithinRange,
    } = getDayStatus(dayItem);
    const customClass = customDaysClassName?.find(day => isSameDay(dayItem, day));
    return ''
      .concat(isToday && !isSelected ? ` -today ${calendarTodayClassName}` : '')
      .concat(!dayItem.isStandard ? ' -blank' : '')
      .concat(dayItem.isWeekend && shouldHighlightWeekends ? ' -weekend' : '')
      .concat(customClass ? ` ${customClass.className}` : '')
      .concat(isSelected ? ` -selected ${calendarSelectedDayClassName}` : '')
      .concat(isStartingDayRange ? ` -selectedStart ${calendarRangeStartClassName}` : '')
      .concat(isEndingDayRange ? ` -selectedEnd ${calendarRangeEndClassName}` : '')
      .concat(isWithinRange ? ` -selectedBetween ${calendarRangeBetweenClassName}` : '')
      .concat(dayItem.isDisabled ? ' -disabled' : '');
  };

  const getViewMonthDays = date => {
    const blanks = createUniqueRange(getMonthFirstWeekday(date), 'starting-blank');
    const days = createUniqueRange(getMonthLength(date)).map(day => ({
      ...day,
      isStandard: true,
      month: date.month,
      year: date.year,
    }));
    return [...blanks, ...days];
  };

  const handleDayPress = ({ isDisabled, ...dayItem }) => {
    if (isDisabled) {
      onDisabledDayError(dayItem);
    } else {
      handleDayClick(dayItem);
    }
  };

  const isDayReachableByKeyboard = ({
    isOnActiveSlide,
    isStandard,
    isSelected,
    isStartingDayRange,
    isToday,
    day,
  }) => {
    if (isQuickSelectorOpen || !isOnActiveSlide || !isStandard) return false;
    return isSelected || isStartingDayRange || isToday || day === 1;
  };

  const renderEachWeekDays = ({ id, value: day, month, year, isStandard }, index) => {
    const dayItem = { day, month, year };
    const isInDisabledDaysRange = disabledDays.some(disabledDay => isSameDay(dayItem, disabledDay));
    const isBeforeMin = isBeforeDate(dayItem, minimumDate);
    const isAfterMax = isBeforeDate(maximumDate, dayItem);
    const isNotValid = isStandard && (isBeforeMin || isAfterMax);
    const isDisabled = isInDisabledDaysRange || isNotValid;
    const isWeekend = weekDaysList.some(
      (weekDayItem, weekDayItemIndex) => weekDayItem.isWeekend && weekDayItemIndex === index,
    );
    const extraClasses = getDayClassNames({
      ...dayItem,
      isWeekend,
      isStandard,
      isDisabled,
    });

    const dayLabel = `${weekDaysList[index].name}, ${day} ${getMonthName(month)} ${year}`;
    const isOnActiveSlide = month === activeDate.month;
    const dayStatus = getDayStatus(dayItem);
    const shouldEnableKeyboard = isDayReachableByKeyboard({
      ...dayItem,
      ...dayStatus,
      isOnActiveSlide,
      isStandard,
    });

    return (
      <div
        tabIndex={shouldEnableKeyboard ? '0' : '-1'}
        key={id}
        className={`Calendar__day -${isRtl ? 'rtl' : 'ltr'} ${extraClasses}`}
        onClick={() => handleDayPress({ ...dayItem, isDisabled })}
        onKeyDown={({ key }) => {
          if (key === 'Enter') handleDayPress({ ...dayItem, isDisabled });
        }}
        aria-disabled={isDisabled}
        aria-label={dayLabel}
        aria-selected={
          dayStatus.isSelected ||
          dayStatus.isStartingDayRange ||
          dayStatus.isEndingDayRange ||
          dayStatus.isWithinRange
        }
        {...(!isStandard || !isOnActiveSlide || isQuickSelectorOpen ? { 'aria-hidden': true } : {})}
        role="gridcell"
        data-is-default-selectable={shouldEnableKeyboard}
      >
        {isStandard ? getLanguageDigits(day) : ''}
      </div>
    );
  };

  const renderMonthDays = isInitialActiveChild => {
    const date = getSlideDate({
      activeDate,
      isInitialActiveChild,
      monthChangeDirection,
      parent: calendarSectionWrapper.current,
    });
    const allDays = getViewMonthDays(date);
    return Array.from({ length: 6 }, (_, weekRowIndex) => (
      <div key={weekRowIndex} className="Calendar__weekRow" role="row">
        {allDays.slice(weekRowIndex * 7, weekRowIndex * 7 + 7).map(renderEachWeekDays)}
      </div>
    ));
  };

  const handleKeyDown = e => {
    handleKeyboardNavigation(e, { allowVerticalArrows: true });
  };

  return (
    <div
      ref={calendarSectionWrapper}
      className="Calendar__sectionWrapper"
      role="presentation"
      data-testid="days-section-wrapper"
      onKeyDown={handleKeyDown}
    >
      <div
        onAnimationEnd={e => {
          handleSlideAnimationEnd(e);
          onSlideChange();
        }}
        className="Calendar__section -shown"
        role="rowgroup"
      >
        {renderMonthDays(true)}
      </div>
      <div
        onAnimationEnd={e => {
          handleSlideAnimationEnd(e);
          onSlideChange();
        }}
        className="Calendar__section -hiddenNext"
        role="rowgroup"
      >
        {renderMonthDays(false)}
      </div>
    </div>
  );
};

export default DaysList;
